import { useState, useEffect, useRef, useMemo } from 'react';
import request from 'lib/request';
import type { OwnershipGraph } from '../types';

const INTERSECT_SHAPES_ENDPOINT = '/queries/intersect_shapes';

interface IntersectShapesResult {
  results: Array<{
    source_name: string;
    target_name: string;
  }>;
}

interface NodeGeometry {
  nodeId: string;
  geometryStr: string;
}

export interface UseIntersectingPropertiesResult {
  intersectingNodeIds: Set<string> | null;
  isLoading: boolean;
  error: string | null;
}

export function getNodeGeometries(graph: OwnershipGraph): {
  all: NodeGeometry[];
  geoToNodeIds: Map<string, string[]>;
} {
  const geoToNodeIds = new Map<string, string[]>();
  const all: NodeGeometry[] = [];

  for (const node of Object.values(graph.nodes)) {
    const polygon = node.property.volume.polygon;
    if (!polygon) continue;
    const geoStr = JSON.stringify(polygon);
    const nodeId = String(node.id);

    const existing = geoToNodeIds.get(geoStr);
    if (existing) {
      existing.push(nodeId);
    } else {
      geoToNodeIds.set(geoStr, [nodeId]);
      all.push({ nodeId, geometryStr: geoStr });
    }
  }
  return { all, geoToNodeIds };
}

// V2 property intersection hook — uses node polygons directly.
// Matches by geometry, not names. Returns intersecting node IDs.
export function useIntersectingProperties(
  selectedPropertyNames: string[],
  graph: OwnershipGraph,
  coverageThreshold: number
): UseIntersectingPropertiesResult {
  const [intersectingNodeIds, setIntersectingNodeIds] =
    useState<Set<string> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cacheRef = useRef<Map<string, Set<string>>>(new Map());
  const abortControllerRef = useRef<AbortController | null>(null);

  const { all: allGeometries, geoToNodeIds } = useMemo(
    () => getNodeGeometries(graph),
    [graph]
  );

  // Map selected property names to their geometry strings
  const selectedGeoStrs = useMemo(() => {
    const nameToGeo = new Map<string, string>();
    for (const node of Object.values(graph.nodes)) {
      const name = node.property.volume.geometryName?.trim();
      const polygon = node.property.volume.polygon;
      if (name && polygon && !nameToGeo.has(name)) {
        nameToGeo.set(name, JSON.stringify(polygon));
      }
    }
    return selectedPropertyNames
      .map((name) => nameToGeo.get(name))
      .filter((g): g is string => g !== undefined);
  }, [selectedPropertyNames, graph]);

  useEffect(() => {
    if (selectedPropertyNames.length === 0 || selectedGeoStrs.length === 0) {
      setIntersectingNodeIds(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    // Build source geometries from selected properties (deduped)
    const uniqueSelectedGeos = [...new Set(selectedGeoStrs)];
    const sourceGeometries = uniqueSelectedGeos.map((geo, i) => ({
      name: `src_${i}`,
      geometry: geo,
    }));

    // Build target geometries — one per unique polygon across all nodes
    const targetGeometries = allGeometries.map((g) => ({
      name: g.nodeId,
      geometry: g.geometryStr,
    }));

    if (sourceGeometries.length === 0 || targetGeometries.length === 0) {
      setIntersectingNodeIds(new Set());
      setIsLoading(false);
      return;
    }

    // Cache check
    const cacheKey = `${uniqueSelectedGeos
      .sort()
      .join('|')}_${coverageThreshold}`;
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      setIntersectingNodeIds(cached);
      setIsLoading(false);
      setError(null);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsLoading(true);
    setError(null);

    request
      .post<IntersectShapesResult[], unknown>(
        INTERSECT_SHAPES_ENDPOINT,
        {
          source_geometries: sourceGeometries,
          target_geometries: targetGeometries,
          coverage_threshold: coverageThreshold,
          allow_self_intersection: true,
        },
        abortController
      )
      .then((response) => {
        if (response.type === 'abort') return;

        if (response.type === 'success' && response.data) {
          const nodeIds = new Set<string>();

          response.data.forEach((resultSet) => {
            resultSet.results?.forEach((row) => {
              // target_name is a node ID — expand to all nodes sharing that geometry
              const targetNodeId = row.target_name;
              const targetGeo = allGeometries.find(
                (g) => g.nodeId === targetNodeId
              );
              if (targetGeo) {
                const allNodeIds = geoToNodeIds.get(targetGeo.geometryStr);
                allNodeIds?.forEach((id) => nodeIds.add(id));
              }
            });
          });

          // Also include all nodes with the same geometry as the selected properties
          uniqueSelectedGeos.forEach((geo) => {
            geoToNodeIds.get(geo)?.forEach((id) => nodeIds.add(id));
          });

          cacheRef.current.set(cacheKey, nodeIds);
          setIntersectingNodeIds(nodeIds);
          setIsLoading(false);
          setError(null);
        } else {
          setError('Failed to fetch intersecting properties');
          setIsLoading(false);
        }
      })
      .catch(() => {
        setError('Failed to fetch intersecting properties');
        setIsLoading(false);
      });

    return () => {
      abortController.abort();
    };
  }, [
    selectedPropertyNames,
    selectedGeoStrs,
    coverageThreshold,
    allGeometries,
    geoToNodeIds,
  ]);

  return { intersectingNodeIds, isLoading, error };
}
